/* eslint-disable react/prop-types */
//https://github.com/zillow/react-slider
import React, { useState, useEffect, createRef, useRef } from "react";
import { toKebabCase } from "src/utilities/utils.js";
import ReactSlider from "react-slider";
import QuizConfirmAnswers from "../../QuizConfirmAnswers/jsx/index.jsx";
import { useQuiz } from "src/contexts/QuizContext.js";
import { quizInteractionTracking } from "src/modules/QuizHelpers/index.js";

function QuizQuestionSlider({
  questionKey,
  sectionKey,
  rangeQuestionName,
  rangeLabelTop,
  rangeLabelBottom,
  rangeEmojis,
  question,
}) {
  const { dispatch, windowWidth } = useQuiz();
  const section = question.subquestions[sectionKey];
  const allOptions = [...section.options].reverse();

  const optionsLength = parseInt(section.options.length);

  let defaultRangeValue = null;
  const rangeAnswer = section.currentAnswer ?? section.savedAnswer;
  if (rangeAnswer) {
    const index = allOptions.indexOf(rangeAnswer);
    defaultRangeValue = index;
  }

  const [rangeValue, setRangeValue] = useState(defaultRangeValue);
  const [currentAnswer, setCurrentAnswer] = useState(section.currentAnswer);

  let refArr = [];

  // set focus
  useEffect(() => {
    if (allOptions.length === refArr.length) {
      allOptions.forEach((option, index) => {
        if (option === section.currentAnswer && refArr[index].current) {
          refArr[index].current.focus();
        }
      });
    }
  }, [refArr, allOptions]);

  useEffect(() => {
    const handler = () => {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    };
    document.addEventListener("mouseup", handler);
    return () => document.removeEventListener("mouseup", handler);
  }, []);

  // handle option click, add small delay for mobile devices to ensure user know's selection has been made
  const handleButton = (e, optionIndex, option) => {
    e.preventDefault();
    // Comprehensive focus reset for mobile devices
    e.target.blur();
    if (document.activeElement && document.activeElement.blur) {
      document.activeElement.blur();
    }
    document.body.focus();
    document.body.blur();

    // Get question number from questionKey (e.g., "question1" -> "Q1")
    const questionNumber = questionKey.replace("question", "Q");

    // Get sub question index from sectionKey (e.g., "section1" -> 1)
    const subQuestionIndex = sectionKey.replace("section", "");
    const subQuestionIndexNum = parseInt(subQuestionIndex, 10) || null;

    // Get selected answer index (1-based)
    const selectedAnswerIndex = optionsLength - optionIndex;

    // Check if answer changed (had a previous answer)
    // Use savedAnswer first (persisted), then currentAnswer (temporary)
    // Only true if there was a previous answer AND it's different from current selection
    const previousAnswer = section.savedAnswer || section.currentAnswer;
    const answerChanged = previousAnswer ? previousAnswer !== option : false;

    // Determine if this is a sub-question (multiple sections) or main question
    const isSubQuestion = Object.keys(question.subquestions).length > 1;

    // Track option selection (same as QuizQuestionOptions)
    quizInteractionTracking({
      interaction_type: "select",
      question_number: questionNumber,
      sub_question_index: isSubQuestion ? subQuestionIndexNum : null,
      selected_answer_index: selectedAnswerIndex,
      answer_changed: answerChanged,
      navigation_action: null,
    });
    
    setRangeValue(optionIndex);
    const type = windowWidth < 1024 ? "saveAnswer" : "currentAnswer";
    const delay = windowWidth < 1024 ? 500 : 0;
    setTimeout(() => {
      dispatch({
        type,
        payload: {
          questionKey: questionKey,
          sectionKey: sectionKey,
          value: option,
        },
      });
      if (delay)
        dispatch({
          type: "qContainerScroll",
        });
    }, delay);
  };

  const props = {
    rangeQuestionName,
    rangeValue,
    optionsLength,
    allOptions,
    setRangeValue,
    setCurrentAnswer,
    currentAnswer,
    payload: {
      questionKey,
      sectionKey,
      value: currentAnswer,
    },
  };

  return question.status == "start" || question.status == "progress" ? (
    <>
      <div className="quiz-question__range">
        {section.title ? (
          <label
            htmlFor={rangeQuestionName}
            className="quiz-question__range-label"
            id={rangeQuestionName}
          >
            {section.title}
          </label>
        ) : (
          ``
        )}
        {rangeLabelTop ? (
          <span className="quiz-question__range-legend legend--max">
            {rangeLabelTop}
          </span>
        ) : (
          ``
        )}

        <div className="quiz-question__range-wrapper">
          <span className="quiz-question__range-extend-top"></span>
          <RangeSlider {...props} />
          <span
            className={`quiz-question__range-extend-bottom ${currentAnswer ? "quiz-question__range-extend-bottom--active" : ""}`}
          ></span>
          <div
            className={`quiz-question__range-buttons range--${optionsLength}`}
          >
            <div className="quiz-question__buttons-marker">
              {allOptions.map((option, optionIndex) => {
                const newRef = createRef();
                refArr.push(newRef);
                return (
                  <button
                    ref={newRef}
                    className={`${option === section.currentAnswer ? "selected" : ""}`}
                    key={option}
                    value={option}
                    tabIndex={allOptions.length - optionIndex}
                    aria-label={`${toKebabCase(`${rangeQuestionName}-${option}`)}`}
                    onClick={(e) => {
                      handleButton(e, optionIndex, option);
                    }}
                  >
                    {rangeEmojis ? (
                      <span
                        className={`quiz-question__buttons-emoji emoji--${optionIndex}`}
                      ></span>
                    ) : (
                      ``
                    )}
                    <span className="quiz-question__buttons-text">
                      {option}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          {rangeLabelBottom ? (
            <span className="quiz-question__range-legend legend--min">
              {rangeLabelBottom}
            </span>
          ) : (
            ``
          )}
        </div>
      </div>
    </>
  ) : (
    windowWidth > 1024 && <QuizConfirmAnswers _question={question} />
  );
}

export const RangeSlider = ({
  rangeQuestionName,
  rangeValue,
  optionsLength,
  currentAnswer,
}) => {
  const sliderRef = useRef();
  useEffect(() => {
    const element = sliderRef.current.querySelector(
      ".quiz-question__range-slider--thumb"
    );
    if (element) element.removeAttribute("tabIndex");
  }, [sliderRef]);
  return (
    <div ref={sliderRef}>
      <ReactSlider
        marks
        className="quiz-question__range-slider"
        thumbClassName="quiz-question__range-slider--thumb"
        thumbActiveClassName="quiz-question__range-slider--thumb--active"
        trackClassName="quiz-question__range-slider--track"
        value={rangeValue}
        ariaLabel={rangeQuestionName}
        ariaLabelledby={rangeQuestionName}
        renderThumb={(props, state) => {
          if (!currentAnswer && !state.value) {
            props.style.background = "transparent";
          }
          return <div {...props}></div>;
        }}
        renderMark={(props) => (
          <div {...props} className="quiz-question__range-slider--mark"></div>
        )}
        min={0}
        max={optionsLength - 1}
        invert={true}
        orientation={"vertical"}
        disabled={true}
      />
    </div>
  );
};

export default QuizQuestionSlider;

/*
export const RangeSlider2 = ({
  rangeQuestionName,
  rangeValue,
  optionsLength,
  setCurrentAnswer,
  currentAnswer,
  setRangeValue,
  allOptions,
  payload,
}) => {
  // Max Value of range slider set by rangeLength
  const { dispatch } = useQuiz();

  // handle range click (desktop)
  const handleOnChange = (value) => {
    setRangeValue(value);
    setCurrentAnswer(allOptions[value]);
  };

  const handleOnAfterChange = (value) => {
    setRangeValue(value);
    setCurrentAnswer(allOptions[value]);
    dispatch({
      type: "currentAnswer",
      payload: { ...payload, value: allOptions[value] },
    });
  };

  return (
    <ReactSlider
      marks
      className="quiz-question__range-slider"
      thumbClassName="quiz-question__range-slider--thumb"
      thumbActiveClassName="quiz-question__range-slider--thumb--active"
      trackClassName="quiz-question__range-slider--track"
      value={rangeValue}
      ariaLabel={rangeQuestionName}
      ariaLabelledby={rangeQuestionName}
      renderThumb={(props, state) => {
        if (!currentAnswer && !state.value) {
          props.style.background = "transparent";
        }
        return <div {...props}></div>;
      }}
      renderMark={(props) => (
        <div {...props} className="quiz-question__range-slider--mark"></div>
      )}
      onChange={handleOnChange}
      onAfterChange={handleOnAfterChange}
      onSliderClick={handleOnAfterChange}
      min={0}
      max={optionsLength - 1}
      invert={true}
      orientation={"vertical"}
    />
  );
};

export default QuizQuestionSlider;*/
